import { ArrowLeft, X as CloseIcon, Globe, Loader2, Puzzle, Zap } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/shared";
import { extensionService, toolService } from "@/lib/services";
import { ColorPicker, IconPicker } from "./IconColorPickers";

export interface CreateExtensionModalProps {
  onClose: () => void;
  onSave: () => void;
}

type Step = "choose" | "custom" | "mcp" | "api";

function buildHeaders(authType: string, headerName: string, value: string): Record<string, string> {
  switch (authType) {
    case "api_key":
      return { [headerName || "X-API-Key"]: value };
    case "bearer":
      return { Authorization: `Bearer ${value}` };
    case "basic":
      return { Authorization: `Basic ${btoa(value)}` };
    default:
      return {};
  }
}

export function CreateExtensionModal({ onClose, onSave }: Readonly<CreateExtensionModalProps>) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("choose");

  // Custom extension state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("puzzle");
  const [color, setColor] = useState("#7C16F5");

  // MCP server state
  const [mcpName, setMcpName] = useState("");
  const [mcpDescription, setMcpDescription] = useState("");
  const [mcpUrl, setMcpUrl] = useState("");
  const [mcpHeaders, setMcpHeaders] = useState("");
  const [mcpTimeout, setMcpTimeout] = useState(60);

  // HTTP API state
  const [apiName, setApiName] = useState("");
  const [apiDescription, setApiDescription] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [apiMethod, setApiMethod] = useState("POST");
  const [authType, setAuthType] = useState("none");
  const [authHeaderName, setAuthHeaderName] = useState("");
  const [authValue, setAuthValue] = useState("");
  const [bodyTemplate, setBodyTemplate] = useState("");
  const [responsePath, setResponsePath] = useState("");

  const [saving, setSaving] = useState(false);

  const handleBack = () => {
    setStep("choose");
  };

  const headerTitleMap: Record<string, string> = {
    choose: "Create Extension",
    custom: "Custom Extension",
    mcp: "MCP Server",
    http: "HTTP API Endpoint",
  };
  const headerTitle = headerTitleMap[step] ?? "Create Extension";

  // -------------------------------------------------------------------------
  // Custom extension submit
  // -------------------------------------------------------------------------
  const handleCustomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      showToast("Please provide an extension name", "warning");
      return;
    }

    try {
      setSaving(true);
      const response = await extensionService.create({
        name: name.trim(),
        description: description.trim() || undefined,
        extensionType: "custom",
        icon: icon.trim() || undefined,
        color: color.trim() || undefined,
      });
      showToast("Extension created — add skills from the detail page", "success");
      onClose();
      navigate(`/extensions/${response.data.uuid}`);
    } catch (err) {
      console.error("Failed to create extension:", err);
      showToast("Failed to create extension", "error");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // MCP server submit
  // -------------------------------------------------------------------------
  const handleMcpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!mcpName.trim()) {
      showToast("Please provide a server name", "warning");
      return;
    }
    if (!mcpUrl.trim()) {
      showToast("Please provide a server URL", "warning");
      return;
    }

    let parsedHeaders: Record<string, string> | undefined;
    if (mcpHeaders.trim()) {
      try {
        parsedHeaders = JSON.parse(mcpHeaders.trim());
      } catch {
        showToast("Headers must be valid JSON", "warning");
        return;
      }
    }

    try {
      setSaving(true);

      // 1. Create the extension container
      await extensionService.create({
        name: mcpName.trim(),
        description: mcpDescription.trim() || undefined,
        extensionType: "custom",
        icon: "server",
        color: "#64748B",
      });

      // 2. Create the MCP tool separately (backend hardcodes tool_type to api_endpoint
      //    when using extensionService.create's tool_config, so we call toolService directly)
      await toolService.create({
        name: mcpName.trim(),
        description: mcpDescription.trim() || `MCP server at ${mcpUrl.trim()}`,
        toolType: "mcp_server",
        config: {
          transport: "http",
          url: mcpUrl.trim(),
          ...(parsedHeaders ? { headers: parsedHeaders } : {}),
          timeout: mcpTimeout,
        },
        icon: "server",
      });

      showToast("MCP Server created successfully", "success");
      onSave();
    } catch (err) {
      console.error("Failed to create MCP server:", err);
      showToast("Failed to create MCP server", "error");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // HTTP API submit
  // -------------------------------------------------------------------------
  const handleApiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiName.trim()) {
      showToast("Please provide a name", "warning");
      return;
    }
    if (!apiUrl.trim()) {
      showToast("Please provide a base URL", "warning");
      return;
    }

    let parsedBody: Record<string, unknown> | undefined;
    if (bodyTemplate.trim()) {
      try {
        parsedBody = JSON.parse(bodyTemplate.trim());
      } catch {
        showToast("Request body template must be valid JSON", "warning");
        return;
      }
    }

    try {
      setSaving(true);
      await extensionService.create({
        name: apiName.trim(),
        description: apiDescription.trim() || undefined,
        extensionType: "custom",
        icon: "zap",
        color: "#10B981",
        toolConfig: {
          url: apiUrl.trim(),
          method: apiMethod,
          headers: buildHeaders(authType, authHeaderName, authValue),
          body_template: parsedBody,
          response_path: responsePath.trim() || undefined,
        },
      });
      showToast("HTTP API Endpoint created successfully", "success");
      onSave();
    } catch (err) {
      console.error("Failed to create HTTP API endpoint:", err);
      showToast("Failed to create HTTP API endpoint", "error");
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary rounded-xl border border-border-primary w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-surface-primary border-b border-border-primary px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {step !== "choose" && (
              <button
                type="button"
                onClick={handleBack}
                className="p-1.5 hover:bg-interactive-hover rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-content-tertiary" />
              </button>
            )}
            <h2 className="text-xl font-semibold text-content-primary">{headerTitle}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-interactive-hover rounded-lg transition-colors">
            <CloseIcon className="w-5 h-5 text-content-tertiary" />
          </button>
        </div>

        {/* Step 1: Choose type */}
        {step === "choose" && (
          <div className="p-6 space-y-3">
            <button
              type="button"
              onClick={() => setStep("custom")}
              className="w-full flex items-start gap-4 p-4 bg-surface-secondary border border-border-secondary rounded-lg hover:border-brand-primary/50 hover:bg-brand-primary/5 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center shrink-0">
                <Puzzle className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <div className="font-medium text-content-primary">Custom Extension</div>
                <p className="text-sm text-content-tertiary mt-0.5">
                  Build a custom extension with skills and configuration
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStep("mcp")}
              className="w-full flex items-start gap-4 p-4 bg-surface-secondary border border-border-secondary rounded-lg hover:border-brand-primary/50 hover:bg-brand-primary/5 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center shrink-0">
                <Globe className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <div className="font-medium text-content-primary">MCP Server</div>
                <p className="text-sm text-content-tertiary mt-0.5">
                  Connect to an external MCP server for additional tool capabilities
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStep("api")}
              className="w-full flex items-start gap-4 p-4 bg-surface-secondary border border-border-secondary rounded-lg hover:border-brand-primary/50 hover:bg-brand-primary/5 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <div className="font-medium text-content-primary">HTTP API Endpoint</div>
                <p className="text-sm text-content-tertiary mt-0.5">
                  Connect a REST API as a tool with custom URL, method, and authentication
                </p>
              </div>
            </button>
          </div>
        )}

        {/* Step 2a: Custom Extension Form */}
        {step === "custom" && (
          <form onSubmit={handleCustomSubmit} className="p-6 space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="ext-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                id="ext-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Extension"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="ext-description" className="block text-sm font-medium text-content-secondary mb-1.5">
                Description
              </label>
              <textarea
                id="ext-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this extension do?"
                rows={3}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none"
              />
            </div>

            {/* Icon & Color */}
            <div className="grid grid-cols-2 gap-4">
              <IconPicker value={icon} onChange={setIcon} label="Icon" />
              <ColorPicker value={color} onChange={setColor} label="Color" />
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 pt-4 border-t border-border-primary">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Creating...
                  </>
                ) : (
                  "Create Extension"
                )}
              </button>
            </div>
          </form>
        )}

        {/* Step 2b: MCP Server Form */}
        {step === "mcp" && (
          <form onSubmit={handleMcpSubmit} className="p-6 space-y-4">
            {/* Server Name */}
            <div>
              <label htmlFor="mcp-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                Server Name <span className="text-red-400">*</span>
              </label>
              <input
                id="mcp-name"
                type="text"
                value={mcpName}
                onChange={(e) => setMcpName(e.target.value)}
                placeholder="My MCP Server"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="mcp-description" className="block text-sm font-medium text-content-secondary mb-1.5">
                Description
              </label>
              <textarea
                id="mcp-description"
                value={mcpDescription}
                onChange={(e) => setMcpDescription(e.target.value)}
                placeholder="What does this MCP server provide?"
                rows={2}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none"
              />
            </div>

            {/* Server URL */}
            <div>
              <label htmlFor="mcp-url" className="block text-sm font-medium text-content-secondary mb-1.5">
                Server URL <span className="text-red-400">*</span>
              </label>
              <input
                id="mcp-url"
                type="url"
                value={mcpUrl}
                onChange={(e) => setMcpUrl(e.target.value)}
                placeholder="https://mcp.example.com/mcp"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
            </div>

            {/* Headers */}
            <div>
              <label htmlFor="mcp-headers" className="block text-sm font-medium text-content-secondary mb-1.5">
                Headers <span className="text-content-quaternary font-normal">(optional, JSON)</span>
              </label>
              <textarea
                id="mcp-headers"
                value={mcpHeaders}
                onChange={(e) => setMcpHeaders(e.target.value)}
                placeholder='{"Authorization": "Bearer ..."}'
                rows={3}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none font-mono text-sm"
              />
            </div>

            {/* Timeout */}
            <div>
              <label htmlFor="mcp-timeout" className="block text-sm font-medium text-content-secondary mb-1.5">
                Timeout <span className="text-content-quaternary font-normal">(seconds)</span>
              </label>
              <div className="relative">
                <input
                  id="mcp-timeout"
                  type="number"
                  value={mcpTimeout}
                  onChange={(e) => setMcpTimeout(Number(e.target.value))}
                  min={1}
                  max={300}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 pr-20"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-quaternary">
                  seconds
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 pt-4 border-t border-border-primary">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Creating...
                  </>
                ) : (
                  "Create MCP Server"
                )}
              </button>
            </div>
          </form>
        )}

        {/* Step 2c: HTTP API Endpoint Form */}
        {step === "api" && (
          <form onSubmit={handleApiSubmit} className="p-6 space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="api-name" className="block text-sm font-medium text-content-secondary mb-1.5">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                id="api-name"
                type="text"
                value={apiName}
                onChange={(e) => setApiName(e.target.value)}
                placeholder="My API Integration"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="api-description" className="block text-sm font-medium text-content-secondary mb-1.5">
                Description
              </label>
              <textarea
                id="api-description"
                value={apiDescription}
                onChange={(e) => setApiDescription(e.target.value)}
                placeholder="What does this API do?"
                rows={2}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none"
              />
            </div>

            {/* Base URL */}
            <div>
              <label htmlFor="api-url" className="block text-sm font-medium text-content-secondary mb-1.5">
                Base URL <span className="text-red-400">*</span>
              </label>
              <input
                id="api-url"
                type="url"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://api.example.com"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
            </div>

            {/* Method & Auth row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="api-method" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Method
                </label>
                <select
                  id="api-method"
                  value={apiMethod}
                  onChange={(e) => setApiMethod(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div>
                <label htmlFor="api-auth-type" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Authentication
                </label>
                <select
                  id="api-auth-type"
                  value={authType}
                  onChange={(e) => setAuthType(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                >
                  <option value="none">None</option>
                  <option value="api_key">API Key (Header)</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Basic Auth</option>
                </select>
              </div>
            </div>

            {/* Auth Header Name (API Key only) */}
            {authType === "api_key" && (
              <div>
                <label htmlFor="api-auth-header" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Auth Header Name
                </label>
                <input
                  id="api-auth-header"
                  type="text"
                  value={authHeaderName}
                  onChange={(e) => setAuthHeaderName(e.target.value)}
                  placeholder="X-API-Key"
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                />
              </div>
            )}

            {/* Auth Value (shown for api_key, bearer, basic) */}
            {authType !== "none" && (
              <div>
                <label htmlFor="api-auth-value" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Auth Value
                </label>
                <input
                  id="api-auth-value"
                  type="password"
                  value={authValue}
                  onChange={(e) => setAuthValue(e.target.value)}
                  placeholder="Enter token or key"
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                />
              </div>
            )}

            {/* Request Body Template (POST/PUT only) */}
            {(apiMethod === "POST" || apiMethod === "PUT") && (
              <div>
                <label htmlFor="api-body-template" className="block text-sm font-medium text-content-secondary mb-1.5">
                  Request Body Template
                </label>
                <textarea
                  id="api-body-template"
                  value={bodyTemplate}
                  onChange={(e) => setBodyTemplate(e.target.value)}
                  placeholder='{"query": "{{input}}"}'
                  rows={4}
                  className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none font-mono text-sm"
                />
              </div>
            )}

            {/* Response Path */}
            <div>
              <label htmlFor="api-response-path" className="block text-sm font-medium text-content-secondary mb-1.5">
                Response Path <span className="text-content-quaternary font-normal">(optional)</span>
              </label>
              <input
                id="api-response-path"
                type="text"
                value={responsePath}
                onChange={(e) => setResponsePath(e.target.value)}
                placeholder="data.results"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
              <p className="text-xs text-content-quaternary mt-1">JSONPath to extract results from the response</p>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 pt-4 border-t border-border-primary">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Creating...
                  </>
                ) : (
                  "Create API Endpoint"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
